import * as Yup from 'yup'
import { Form, Row, Collapse, Accordion, Table } from 'react-bootstrap'
import { CustomAccordionButton, CustomFormControl, CustomLink } from '../../../../StyledComponents';
import { useState } from 'react';
import { characterRestrictionInDocNamesRegExp } from '../ValidationRules';

function getFileSizeAsString(totalBytes: number): string {
    const Gigabyte = 1073741824
    const Megabyte = 1048576
    const Kilobyte = 1024

    if (totalBytes <= 0) return "0 Bytes"

    if (totalBytes >= Gigabyte) {
        return (totalBytes / Gigabyte).toFixed(2) + " GB"
    } else if (totalBytes >= Megabyte) {
        return (totalBytes / Megabyte).toFixed(2) + " MB"
    } else if (totalBytes >= Kilobyte) {
        return (totalBytes / Kilobyte).toFixed(2) + " kB"
    } else {
        return totalBytes + " Bytes"
    }
}

const checkDokument = (settings) => (Yup.mixed()
    .test("FileSize", `Dokument darf nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`, (file) => {
        if (file) 
        {
            if (file.isValid === undefined || file.isValid === null) 
            {
                file.isValid = true;
            }
            else
            {
                file.isValid = file.size < settings.applicationFileSizeLimit;
                if (!file.isValid) file.errMessage = `Dokument darf nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`;
            }
            return file.isValid;
        } 
        else 
        {
            return true;
        }
    })
    .test("NullByteTest", "Die Datei darf nicht leer sein.", (file) => {
        if (file) 
        {
            if (file.isValid === undefined || file.isValid === null) 
            {
                file.isValid = true;
            }
            else if (file.isValid) 
            {
                file.isValid = file.size > 0;
                if (!file.isValid) file.errMessage = "Die Datei darf nicht leer sein.";
            }
            return file.isValid;
        } 
        else 
        {
            return true;
        }
    })
    .test("FileFormat", `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`, (file) => {
        if (file) 
        {
            if (file.isValid === undefined || file.isValid === null) 
            {
                file.isValid = true;
            }
            else if (file.isValid) 
            {
                file.isValid = settings.applicationPermittedMimeTypes.includes(file.type);
                if (!file.isValid) file.errMessage = `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`;
            }
            return file.isValid;
        } 
        else 
        {
            return true;
        }
    })
    .when("settings.isUnicode", {
        is: () => !settings.isUnicode,
        then: Yup.mixed()
            .test("FileNameTest",
                `Ungültiger Dokumentenname`,
                (file) => {
                    if (file) 
                    {
                        if (file.isValid === undefined || file.isValid === null) 
                        {
                            file.isValid = true;
                        }
                        else if (file.isValid) 
                        {
                            file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                            if (!file.isValid) file.errMessage = "Ungültiger Dokumentenname";
                        }
                        return file.isValid;
                    } 
                    else 
                    {
                        return true;
                    }
                })
    }));

export const Schema = settings => (Yup.object().shape({
    cvFile: checkDokument(settings),
    completeApplicationFile: checkDokument(settings),
    letterFile: checkDokument(settings),
    applicantPhotoFile: checkDokument(settings),
    certificateOfEnrollmentFile: checkDokument(settings),
    compulsoryTraineeshipCertificateFile: checkDokument(settings),
    schoolCertificateFile: checkDokument(settings),
    additionalFiles: Yup.array()
        .nullable()
        .test("FileSize",
            `Die Unterlagen dürfen höchstens ${settings.applicationFileNumberLimit} Dateien und zusammen nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`,
            (files) => {
                if (files && files.length > 0) {
                    let totalSize: number = files.reduce((accumulator, currentFile) => accumulator + currentFile.size, 0);
                    return totalSize <= settings.applicationFileSizeLimit && files.length <= settings.applicationFileNumberLimit;
                } else {
                    return true;
                }
            })
        .test("FileFormat",
            `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`,
            (files) => {
                if (files && files.length > 0) {
                    let allValid = true;
                    for (let file of files) {
                        if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                        if (!settings.applicationPermittedMimeTypes.includes(file.type)) {
                            file.isValid = false;
                            file.errMessage = `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`;
                            allValid = false;
                        }
                    }
                    return allValid;
                } else {
                    return true;
                }
            })
        .when("settings.isUnicode", {
            is: () => !settings.isUnicode,
            then: Yup.array().nullable()
                .test("FileNameTest",
                    `Ungültiger Dokumentenname`,
                    (files) => {
                        if (files && files.length > 0) {
                            let allValid = true;
                            for (let file of files) {
                                if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                if (!file.name.match(characterRestrictionInDocNamesRegExp)) {
                                    file.isValid = false;
                                    file.errMessage = `Ungültiger Dokumentenname`;
                                    allValid = false;
                                }
                            }
                            return allValid;
                        } else {
                            return true;
                        }
                    })
        }),
}));

class FileExt extends File {
    isValid: boolean = true;
    errMessage: string = "";
}

export const DocumentsCertificatesSubForm = ({ form: { values, setFieldTouched, setFieldValue, handleBlur, handleChange, touched, errors }, ...props }) => {
    const { colors, settings } = props;
    const [cvDocument, setCvDocument] = useState<FileExt>();
    const [completeApplicationDocument, setCompleteApplicationDocument] = useState<FileExt>();
    const [letterDocument, setLetterDocument] = useState<FileExt>();
    const [applicantPhotoDocument, setApplicantPhotoDocument] = useState<FileExt>();
    const [certificateOfEnrollmentDocument, setCertificateOfEnrollmentDocument] = useState<FileExt>();
    const [compulsoryTraineeshipCertificateDocument, setCompulsoryTraineeshipCertificateDocument] = useState<FileExt>();
    const [schoolCertificateDocument, setSchoolCertificateDocument] = useState<FileExt>();
    const [additionalDocuments, setAdditionalDocuments] = useState<FileExt[]>([]);

    function getTotalSizeOfAdditionalDocs(): number {
        if (!additionalDocuments || additionalDocuments?.length === 0) return 0

        let totalSize: number = 0
        for (let i = 0; i < additionalDocuments.length; i++) {
            const element = additionalDocuments[i];
            totalSize += element.size
        }
        return totalSize
    }

    function getTotalSizeOfDocs(): number {
        let totalSizeOfAddititionDocs = getTotalSizeOfAdditionalDocs();
        let cvDocumentSize = cvDocument ? cvDocument.size : 0;
        let completeApplicationDocumentSize = completeApplicationDocument ? completeApplicationDocument.size : 0;
        let letterDocumentSize = letterDocument ? letterDocument.size : 0;
        let applicantPhotoDocumentSize = applicantPhotoDocument ? applicantPhotoDocument.size : 0;
        let certificateOfEnrollmentDocumentSize = certificateOfEnrollmentDocument ? certificateOfEnrollmentDocument.size : 0;
        let compulsoryTraineeshipCertificateDocumentSize = compulsoryTraineeshipCertificateDocument ? compulsoryTraineeshipCertificateDocument.size : 0;
        let schoolCertificateDocumentSize = schoolCertificateDocument ? schoolCertificateDocument.size : 0;
        return totalSizeOfAddititionDocs + cvDocumentSize + completeApplicationDocumentSize + letterDocumentSize + applicantPhotoDocumentSize + certificateOfEnrollmentDocumentSize + compulsoryTraineeshipCertificateDocumentSize + schoolCertificateDocumentSize;
    }

    return (
        <Row>
            <Form.Group className="mb-3" controlId="formCvDoc">
                <Form.Label>Lebenslauf {cvDocument && "(" + getFileSizeAsString(cvDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="cvFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCvDocument(event.target.files[0]);
                            setFieldTouched("cvFile");
                            setFieldValue("cvFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.cvFile && !errors.cvFile}
                    isInvalid={touched.cvFile && errors.cvFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.cvFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCompleteApplicationDoc">
                <Form.Label>Komplette Bewerbung {completeApplicationDocument && "(" + getFileSizeAsString(completeApplicationDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="completeApplicationFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCompleteApplicationDocument(event.target.files[0]);
                            setFieldTouched("completeApplicationFile");
                            setFieldValue("completeApplicationFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.completeApplicationFile && !errors.completeApplicationFile}
                    isInvalid={touched.completeApplicationFile && errors.completeApplicationFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.completeApplicationFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formLetterDoc">
                <Form.Label>Anschreiben {letterDocument && "(" + getFileSizeAsString(letterDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="letterFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setLetterDocument(event.target.files[0]);
                            setFieldTouched("letterFile");
                            setFieldValue("letterFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.letterFile && !errors.letterFile}
                    isInvalid={touched.letterFile && errors.letterFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.letterFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formApplicantPhotoDoc">
                <Form.Label>Foto {applicantPhotoDocument && "(" + getFileSizeAsString(applicantPhotoDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="applicantPhotoFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setApplicantPhotoDocument(event.target.files[0]);
                            setFieldTouched("applicantPhotoFile");
                            setFieldValue("applicantPhotoFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.applicantPhotoFile && !errors.applicantPhotoFile}
                    isInvalid={touched.applicantPhotoFile && errors.applicantPhotoFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.applicantPhotoFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCertificateOfEnrollmentDoc">
                <Form.Label>Für Studierende: Immatrikulationsbescheinigung {certificateOfEnrollmentDocument && "(" + getFileSizeAsString(certificateOfEnrollmentDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="certificateOfEnrollmentFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCertificateOfEnrollmentDocument(event.target.files[0]);
                            setFieldTouched("certificateOfEnrollmentFile");
                            setFieldValue("certificateOfEnrollmentFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.certificateOfEnrollmentFile && !errors.certificateOfEnrollmentFile}
                    isInvalid={touched.certificateOfEnrollmentFile && errors.certificateOfEnrollmentFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.certificateOfEnrollmentFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCompulsoryTraineeshipCertificateDoc">
                <Form.Label>Für Studierende: Nachweis Pflichtpraktikum {compulsoryTraineeshipCertificateDocument && "(" + getFileSizeAsString(compulsoryTraineeshipCertificateDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="compulsoryTraineeshipCertificateFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCompulsoryTraineeshipCertificateDocument(event.target.files[0]);
                            setFieldTouched("compulsoryTraineeshipCertificateFile");
                            setFieldValue("compulsoryTraineeshipCertificateFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.compulsoryTraineeshipCertificateFile && !errors.compulsoryTraineeshipCertificateFile}
                    isInvalid={touched.compulsoryTraineeshipCertificateFile && errors.compulsoryTraineeshipCertificateFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.compulsoryTraineeshipCertificateFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formSchoolCertificateDoc">
                <Form.Label>Für Schüler*innen: Schulbescheinigung {schoolCertificateDocument && "(" + getFileSizeAsString(schoolCertificateDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="schoolCertificateFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setSchoolCertificateDocument(event.target.files[0]);
                            setFieldTouched("schoolCertificateFile");
                            setFieldValue("schoolCertificateFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.schoolCertificateFile && !errors.schoolCertificateFile}
                    isInvalid={touched.schoolCertificateFile && errors.schoolCertificateFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.schoolCertificateFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formAdditionalFiles">
                <Form.Label>Weitere Unterlagen (optional) {additionalDocuments && getTotalSizeOfAdditionalDocs() > 0 && "(" + getFileSizeAsString(getTotalSizeOfAdditionalDocs()) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    multiple
                    name="additionalFiles"
                    onChange={(event) => {

                        if (event.target.files?.length) {
                            let files: FileExt[] = [];
                            if (additionalDocuments.length > 0) {
                                files = additionalDocuments.slice();
                            }
                            files.push(...Array.from<FileExt>(event.target.files));

                            setAdditionalDocuments(files);
                            setFieldTouched("additionalFiles");
                            setFieldValue("additionalFiles", files || undefined);
                        }

                    }}
                    isValid={touched.additionalFiles && !errors.additionalFiles}
                    isInvalid={errors.additionalFiles}
                    accept={settings.applicationPermittedExtensions} />
                <Form.Control.Feedback type="invalid">
                    {errors.additionalFiles}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: errors.additionalFiles ? "none" : "flex" }}>
                    Mehrfachauswahl möglich: Maximal {settings.applicationFileNumberLimit} Dateien (Lebenslauf + {settings.applicationFileNumberLimit-1} weitere Dateien) mit insgesamt {getFileSizeAsString(settings.applicationFileSizeLimit)}
                </Form.Text>
            </Form.Group>

            <div style={{ marginBottom: "16px" }}>
                <Collapse in={getTotalSizeOfDocs() > 0}>
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <CustomAccordionButton colors={colors}>Ausgewählte Dateien ({getFileSizeAsString(getTotalSizeOfDocs())})</CustomAccordionButton>
                            <Accordion.Body>
                                <Table>
                                    <tbody>
                                        {cvDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={cvDocument.isValid ? "" : "text-danger"}><strong>{cvDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(cvDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCvDocument(undefined);
                                                        setFieldTouched("cvFile");
                                                        setFieldValue("cvFile", undefined);

                                                        let inputElement = document.getElementById("formCvDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={cvDocument.errMessage} style={{ visibility: cvDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {completeApplicationDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={completeApplicationDocument.isValid ? "" : "text-danger"}><strong>{completeApplicationDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(completeApplicationDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCompleteApplicationDocument(undefined);
                                                        setFieldTouched("completeApplicationFile");
                                                        setFieldValue("completeApplicationFile", undefined);

                                                        let inputElement = document.getElementById("formCompleteApplicationDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={completeApplicationDocument.errMessage} style={{ visibility: completeApplicationDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {letterDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={letterDocument.isValid ? "" : "text-danger"}><strong>{letterDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(letterDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setLetterDocument(undefined);
                                                        setFieldTouched("letterFile");
                                                        setFieldValue("letterFile", undefined);

                                                        let inputElement = document.getElementById("formLetterDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={letterDocument.errMessage} style={{ visibility: letterDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {applicantPhotoDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={applicantPhotoDocument.isValid ? "" : "text-danger"}><strong>{applicantPhotoDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(applicantPhotoDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setApplicantPhotoDocument(undefined);
                                                        setFieldTouched("applicantPhotoFile");
                                                        setFieldValue("applicantPhotoFile", undefined);

                                                        let inputElement = document.getElementById("formApplicantPhotoDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={applicantPhotoDocument.errMessage} style={{ visibility: applicantPhotoDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {certificateOfEnrollmentDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={certificateOfEnrollmentDocument.isValid ? "" : "text-danger"}><strong>{certificateOfEnrollmentDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(certificateOfEnrollmentDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCertificateOfEnrollmentDocument(undefined);
                                                        setFieldTouched("certificateOfEnrollmentFile");
                                                        setFieldValue("certificateOfEnrollmentFile", undefined);

                                                        let inputElement = document.getElementById("formCertificateOfEnrollmentDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={certificateOfEnrollmentDocument.errMessage} style={{ visibility: certificateOfEnrollmentDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {compulsoryTraineeshipCertificateDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={compulsoryTraineeshipCertificateDocument.isValid ? "" : "text-danger"}><strong>{compulsoryTraineeshipCertificateDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(compulsoryTraineeshipCertificateDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCompulsoryTraineeshipCertificateDocument(undefined);
                                                        setFieldTouched("compulsoryTraineeshipCertificateFile");
                                                        setFieldValue("compulsoryTraineeshipCertificateFile", undefined);

                                                        let inputElement = document.getElementById("formCompulsoryTraineeshipCertificateDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={compulsoryTraineeshipCertificateDocument.errMessage} style={{ visibility: compulsoryTraineeshipCertificateDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {schoolCertificateDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={schoolCertificateDocument.isValid ? "" : "text-danger"}><strong>{schoolCertificateDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(schoolCertificateDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setSchoolCertificateDocument(undefined);
                                                        setFieldTouched("schoolCertificateFile");
                                                        setFieldValue("schoolCertificateFile", undefined);

                                                        let inputElement = document.getElementById("formSchoolCertificateDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={schoolCertificateDocument.errMessage} style={{ visibility: schoolCertificateDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {additionalDocuments && Array.from(additionalDocuments).map((file: FileExt) =>
                                            <tr key={file.name}>
                                                <td style={{ wordBreak: "break-all" }} className={file.isValid === undefined || file.isValid ? "" : "text-danger"}>{file.name}</td>
                                                <td>{getFileSizeAsString(file.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        let pos = -1;
                                                        for (let i = 0; i < additionalDocuments.length; i++) {
                                                            const f: FileExt = additionalDocuments[i]
                                                            if (f.name === file.name) {
                                                                pos = i;
                                                                break;
                                                            }
                                                        }

                                                        if (pos > -1) {
                                                            let files: FileExt[] = [];
                                                            files = additionalDocuments.slice();
                                                            files.splice(pos, 1);

                                                            setAdditionalDocuments(files);
                                                            setFieldValue("additionalFiles", files || undefined);

                                                            if (files.length === 0) {
                                                                let inputElement = document.getElementById("formAdditionalFiles") as HTMLInputElement;
                                                                inputElement.value = "";
                                                            }
                                                        }
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={file.errMessage} style={{ visibility: file.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Collapse>
            </div>
        </Row>
    );
};
